import { pick } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { listTablePrefs } from 'app/constants'
import type InferActionParams from 'core/actions/InferActionParams'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import DocumentMeta from 'core/components/DocumentMeta'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import { createGridStatusCell, type StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { listKaapiClusterAddons, updateKaapiClusterAddon } from '../kaapi/cluster-addons/actions'
import { kaapiClusterAddonsSelector } from '../kaapi/cluster-addons/selectors'

type ActionParams = InferActionParams<typeof listKaapiClusterAddons>
type Params = ActionParams & {}

const defaultParams: Params = {
  namespace: '',
}

const usePrefParams = createUsePrefParamsHook('Cluster Addons', listTablePrefs)

const Phase = {
  Provisioning: 'Provisioning',
  Provisioned: 'Provisioned',
  FailedNonRetriable: 'FailedNonRetriable',
  Failed: 'Failed',
  Removing: 'Removing',
  Removed: 'Removed',
  Unknown: 'Unknown',
}

const getPhaseStatus = (phase: string, tooltipBody?: string): StatusCellModel => {
  switch (phase) {
    case Phase.Provisioned:
      return { variant: 'success', label: phase, tooltipBody }
    case Phase.Provisioning:
      return { variant: 'warning', label: phase, tooltipBody }
    case Phase.Failed:
    case Phase.FailedNonRetriable:
    case Phase.Removing:
    case Phase.Removed:
      return { variant: 'error', label: phase, tooltipBody }
    default:
      return { variant: 'unknown', label: Phase.Unknown, tooltipBody }
  }
}

const getColumns = (handleOpenUpdateModal) => [
  {
    key: 'spec.type',
    label: 'Name',
  },
  {
    key: 'spec.version',
    label: 'Version',
  },
  {
    key: 'status',
    label: 'Status',
    CellComponent: createGridStatusCell({
      dataFn: (phase: any): StatusCellModel => {
        const phaseStatus = phase?.status
        const failureMessage = phase?.failureMessage
        return getPhaseStatus(phaseStatus || 'Provisioning', failureMessage)
      },
    }),
  },
  {
    key: 'status.upgradeInfo',
    label: '',
    CellComponent: (props) => {
      return (
        <>
          {props?.item?.status?.upgradeInfo?.available && (
            <SimpleLink
              onClick={() => handleOpenUpdateModal(props?.item)}
              data-testid="update-addon-link"
            >
              Update Available ({props?.item?.status?.upgradeInfo?.available})
            </SimpleLink>
          )}
        </>
      )
    },
  },
]

export default function ListClusterAddons() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [openUpdateModal, setOpenUpdateModal] = useState(false)
  const [version, setVersion] = useState('')
  const [updateAvailable, setUpdateAvailable] = useState('')
  const [name, setName] = useState('')

  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')
  const clustersAddons = useSelector(kaapiClusterAddonsSelector) || []

  // Load immediately if data empty; defer if already cached
  const { loading: loadingKaapiClusterAddons, reload: reloadKaapiClusterAddons } = useListAction(
    listKaapiClusterAddons,
    { loadOnDemand: clustersAddons.length > 0 },
  )
  const { update: updateAddonFn, updating, error, reset } = useUpdateAction(updateKaapiClusterAddon)

  const handleOpenUpdateModal = useCallback((item) => {
    setVersion(item?.spec?.version)
    setUpdateAvailable(item?.status?.upgradeInfo?.available)
    setName(item?.spec?.type)
    setOpenUpdateModal(true)
  }, [])

  const filteredClusterAddons = useMemo(
    () =>
      clustersAddons?.filter(
        (clusterAddon) => clusterAddon?.spec?.clusterID === k8sPluginGlobalPerfs?.cluster,
      ),
    [clustersAddons, k8sPluginGlobalPerfs],
  )

  const handleSubmit = useCallback(async () => {
    const body = {
      spec: {
        version: updateAvailable,
      },
    }
    const { success } = await updateAddonFn({
      namespace: filteredClusterAddons?.[0]?.metadata?.namespace,
      customBody: body,
      name: `${name}-addon-${k8sPluginGlobalPerfs?.cluster}`,
    })
    if (success) {
      reset()
      reloadKaapiClusterAddons(true, true)
      setOpenUpdateModal(false)
    }
  }, [
    updateAvailable,
    k8sPluginGlobalPerfs,
    updateAddonFn,
    filteredClusterAddons?.[0]?.metadata?.namespace,
    reset,
    reloadKaapiClusterAddons,
  ])

  return (
    <>
      <DocumentMeta title="Cluster Addons Overview" data-testid="cluster-addons-overview" />
      {openUpdateModal && (
        <ConfirmationDialog
          title="Update Version"
          text={`${name} version will be updated from ${version} to ${updateAvailable}`}
          onConfirm={handleSubmit}
          onCancel={() => setOpenUpdateModal(false)}
          error={error}
          loading={loadingKaapiClusterAddons || updating}
          open
          data-testid="update-metalLB-dialog"
        />
      )}
      <ListContainer<any, any>
        searchTargets={['spec.type']}
        uniqueIdentifier="uid"
        loading={loadingKaapiClusterAddons}
        loadingMessage="Loading..."
        onRefresh={reloadKaapiClusterAddons}
        data={filteredClusterAddons}
        columns={getColumns(handleOpenUpdateModal)}
        getParamsUpdater={getParamsUpdater}
        label="Cluster Addons"
        disableRowSelection
        showItemsCountInLabel
        showBreadcrumbs={false}
        data-testid="cluster-addons-list-container"
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
